import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ConversationService,
  Conversation,
  ConversationMessage,
  MessageRating,
} from "../services/conversationService";
import {
  MCPService,
  MCPModelCatalog,
  AttachedFile,
  ChatModelId,
  MODEL_SURFACE_DEFAULT,
  FALLBACK_CLIENT_MODEL,
  codenameForModel,
  normalizeAiMessageText,
} from "../services/mcpService";
import {
  canSendMessage,
  UsageStatus,
} from "../services/mcpUsageService";

// Types
export interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  sessionId?: string;
  inputTokens?: number;
  outputTokens?: number;
  executionTimeMs?: number;
  attachments?: AttachedFile[];
  createdAt?: string;
  metadata?: Record<string, unknown>;
  // AI message lifecycle. "pending" = placeholder awaiting the reply (renders as
  // "Thinking..."), "error" = the reply failed (renders an error bubble + Retry).
  // Undefined for user messages and legacy AI messages (treated as complete).
  status?: "pending" | "complete" | "error";
  // Owner's thumbs up/down on an AI response (undefined = not rated). See MCPMessage.rating.
  rating?: MessageRating;
  ratingComment?: string;
}

// Single source of truth for turning a raw GraphQL message into a renderable
// ChatMessage — shared by the analyst and project chat so both render identically.
export function mapConversationMessage(msg: ConversationMessage): ChatMessage {
  let attachments: AttachedFile[] | undefined;
  if (msg.attachments) {
    // Guard so one malformed attachments blob can't reject the whole conversation load
    try {
      attachments = JSON.parse(msg.attachments);
    } catch {
      attachments = undefined;
    }
  }
  return {
    id: msg.id,
    // A "pending" placeholder is intentionally empty (renders as "Thinking...")
    text: msg.status === "pending" ? msg.content : normalizeAiMessageText(msg.content, msg.isUser),
    isUser: msg.isUser,
    sessionId: msg.sessionId,
    inputTokens: msg.inputTokens,
    outputTokens: msg.outputTokens,
    executionTimeMs: msg.executionTimeMs,
    attachments,
    createdAt: msg.createdAt,
    status: msg.status,
    rating: msg.rating ?? undefined,
    ratingComment: msg.ratingComment ?? undefined,
  };
}

export interface ConversationListItem {
  id: string;
  title: string;
  model: ChatModelId;
  modelCodename: string;
  messageCount: number;
  unreadCount: number;
  lastMessageAt: string;
}

type LoadingState = "idle" | "pending" | "success" | "failed";

export interface AiChatState {
  // Messages for current conversation
  messages: ChatMessage[];
  messagesLoading: LoadingState;

  // Current conversation metadata
  currentConversation: Conversation | null;

  // Conversation list (sidebar)
  conversations: ConversationListItem[];
  conversationsLoading: LoadingState;

  // Input state
  inputValue: string;
  drafts: Record<string, string>;
  model: ChatModelId;
  // Server-owned model catalog (GET /mcp/models) — the bundle never names a vendor or tier.
  // null until it loads; tiers stay empty for non-admins.
  modelCatalog: MCPModelCatalog | null;
  // Admin-only: pins this chat to one model for A/B testing; '' runs the chat surface's
  // configured model. Ignored server-side for non-admins.
  modelOverride: ChatModelId;
  // Admin-only: when true, the request asks the router to stream per-round
  // reasoning, rendered as collapsible <thinking> blocks. Ignored for non-admins.
  debug: boolean;
  attachedFiles: AttachedFile[];
  // Track number of in-flight uploads (supports parallel uploads)
  uploadingCount: number;

  // Sending state
  isSending: boolean;
  sendError: string | null;
  // Track which conversation a pending send belongs to (guards against navigation during async)
  pendingSendConversationId: string | null;
  // Track when message was sent for timeout detection
  pendingSendTimestamp: string | null;

  // Token tracking for active conversation
  activeConversationTokens: number;

  // Pre-generated UUID for new chats — used as GCS upload path and MCPConversation id
  pendingConversationId: string;

  // Per-message proposal state keyed by [conversationId][messageId] to allow per-conversation cleanup
  proposalStatuses: Record<string, Record<string, 'pending' | 'approved' | 'declined'>>;
  proposalSelectedGroups: Record<string, Record<string, string>>;
  proposalAssetImages: Record<string, Record<string, (string | null)[]>>;
  proposalGroupImages: Record<string, Record<string, string | null>>;
  proposalAssetEdits: Record<string, Record<string, Array<{ name: string; description: string }>>>;
  proposalGroupEdit: Record<string, Record<string, { name: string; description: string }>>;
  proposalApprovedGroupIds: Record<string, Record<string, string>>;
  // Schema id minted for a model-deploy proposal card, persisted so a retry after a mid-deploy
  // failure reuses it (survives remount) instead of orphaning a half-created schema.
  proposalDeploySchemaIds: Record<string, Record<string, string>>;
}

const initialState: AiChatState = {
  messages: [],
  messagesLoading: "idle",
  currentConversation: null,
  conversations: [],
  conversationsLoading: "idle",
  inputValue: "",
  drafts: {},
  model: FALLBACK_CLIENT_MODEL,
  modelCatalog: null,
  modelOverride: MODEL_SURFACE_DEFAULT,
  debug: false,
  attachedFiles: [],
  uploadingCount: 0,
  isSending: false,
  sendError: null,
  pendingSendConversationId: null,
  pendingSendTimestamp: null,
  activeConversationTokens: 0,
  pendingConversationId: crypto.randomUUID(),
  proposalStatuses: (() => {
    try { return JSON.parse(localStorage.getItem('proposalStatuses') || '{}'); } catch { return {}; }
  })(),
  proposalSelectedGroups: (() => {
    try { return JSON.parse(localStorage.getItem('proposalSelectedGroups') || '{}'); } catch { return {}; }
  })(),
  proposalAssetImages: (() => {
    try { return JSON.parse(localStorage.getItem('proposalAssetImages') || '{}'); } catch { return {}; }
  })(),
  proposalGroupImages: (() => {
    try { return JSON.parse(localStorage.getItem('proposalGroupImages') || '{}'); } catch { return {}; }
  })(),
  proposalAssetEdits: (() => {
    try { return JSON.parse(localStorage.getItem('proposalAssetEdits') || '{}'); } catch { return {}; }
  })(),
  proposalGroupEdit: (() => {
    try { return JSON.parse(localStorage.getItem('proposalGroupEdit') || '{}'); } catch { return {}; }
  })(),
  proposalApprovedGroupIds: (() => {
    try { return JSON.parse(localStorage.getItem('proposalApprovedGroupIds') || '{}'); } catch { return {}; }
  })(),
  proposalDeploySchemaIds: (() => {
    try { return JSON.parse(localStorage.getItem('proposalDeploySchemaIds') || '{}'); } catch { return {}; }
  })(),
};

// Async Thunks

// Fetch the model catalog. Kept server-side so the bundle never names a vendor or tier; every
// picker renders whatever this returns. Idempotent — callers can fire it on mount.
export const fetchModelCatalog = createAsyncThunk(
  "aiChat/fetchModelCatalog",
  async (_: void, thunkAPI) => {
    try {
      return await MCPService.getModels();
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error instanceof Error ? error.message : "Failed to fetch models"
      );
    }
  }
);

// Fetch conversation list (sidebar)
export const fetchConversations = createAsyncThunk(
  "aiChat/fetchConversations",
  async (limit: number = 20, thunkAPI) => {
    try {
      const conversations = await ConversationService.getUserConversations(
        limit
      );
      return conversations.map((c) => ({
        id: c.id,
        title: c.title,
        model: c.model,
        modelCodename: c.modelCodename,
        messageCount: c.messageCount ?? 0,
        unreadCount: c.unreadCount ?? 0,
        lastMessageAt: c.lastMessageAt,
      }));
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error instanceof Error ? error.message : "Failed to fetch conversations"
      );
    }
  }
);

// Select/load a conversation with messages
export const selectConversation = createAsyncThunk(
  "aiChat/selectConversation",
  async (conversationId: string, thunkAPI) => {
    try {
      const fullConversation =
        await ConversationService.getConversationWithMessages(conversationId);
      if (!fullConversation) {
        return thunkAPI.rejectWithValue("Conversation not found");
      }

      const messages: ChatMessage[] = (fullConversation.messages?.items ?? [])
        .sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        )
        .map(mapConversationMessage);

      // Calculate total tokens for this conversation
      const totalTokens = messages.reduce((sum, msg) => {
        return sum + (msg.inputTokens ?? 0) + (msg.outputTokens ?? 0);
      }, 0);

      return { conversation: fullConversation, messages, totalTokens };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error instanceof Error ? error.message : "Failed to load conversation"
      );
    }
  }
);

// Error types for sendMessage rejections
export interface SendMessageLimitError {
  type: "limit_exceeded";
  usageStatus: UsageStatus;
}

export interface SendMessageNoSubscriptionError {
  type: "no_subscription";
  usageStatus: UsageStatus;
}

export interface SendMessageError {
  type: "error";
  message: string;
}

export type SendMessageRejection = SendMessageLimitError | SendMessageNoSubscriptionError | SendMessageError;

// Send a message
export const sendMessage = createAsyncThunk(
  "aiChat/sendMessage",
  async (
    {
      inputValue,
      attachedFilesInput,
      userId,
      persona,
      isAdmin,
      promptOverride,
      onboarding,
    }: {
      inputValue: string;
      attachedFilesInput: AttachedFile[];
      userId: string | null;
      persona?: string;
      isAdmin?: boolean;
      promptOverride?: string;
      onboarding?: boolean;
    },
    thunkAPI
  ) => {
    const state = thunkAPI.getState() as { aiChat: AiChatState };
    const { messages, currentConversation, model, modelCatalog, modelOverride, pendingConversationId, debug } = state.aiChat;
    // Best guess for the optimistic record — a pinned surface model is only known server-side, so
    // the lambda corrects the conversation once the answer lands (asyncHandler updateConversationMetadata).
    // Never record modelOverride: an admin pin is a vendor id, and these rows are org-readable.
    const recordedModel = model || modelCatalog?.default || FALLBACK_CLIENT_MODEL;
    const recordedCodename = codenameForModel(recordedModel, modelCatalog);
    const attachedFiles = attachedFilesInput;

    // Capture the conversation context at send time to guard against navigation during async operations
    const startingConversationId = currentConversation?.id ?? null;

    const trimmed = inputValue.trim();
    if (!trimmed) {
      return thunkAPI.rejectWithValue({
        type: "error",
        message: "Empty message",
      } as SendMessageError);
    }

    // Check usage limit and subscription status
    if (userId) {
      const { allowed, usageStatus, reason } = await canSendMessage(userId, isAdmin);
      if (!allowed) {
        if (reason === 'no_subscription') {
          return thunkAPI.rejectWithValue({
            type: "no_subscription",
            usageStatus,
          } as SendMessageNoSubscriptionError);
        }
        return thunkAPI.rejectWithValue({
          type: "limit_exceeded",
          usageStatus,
        } as SendMessageLimitError);
      }
    }

    try {
      // Filter out the optimistic pending message added by the pending reducer
      // This ensures isFirstMessage is calculated correctly
      const messagesBeforeOptimistic = messages.filter(
        (m) => !m.id.endsWith("-pending")
      );
      const isFirstMessage = messagesBeforeOptimistic.length === 0;
      let conversationForSaving = currentConversation;

      // Create conversation if first message, using the pre-generated UUID
      if (isFirstMessage && !conversationForSaving) {
        conversationForSaving = await ConversationService.createConversation({
          id: pendingConversationId,
          title: ConversationService.generateTitle(trimmed),
          model: recordedModel,
          modelCodename: recordedCodename,
        });
      }

      // Save user message to AppSync
      if (conversationForSaving) {
        await ConversationService.addMessage({
          conversationId: conversationForSaving.id,
          content: trimmed,
          isUser: true,
          model: recordedModel,
          modelCodename: recordedCodename,
          attachments: attachedFiles.length > 0
            ? JSON.stringify(attachedFiles)
            : undefined,
        });
      }

      // Build prompt for MCP — use promptOverride if provided (e.g. asset context injected silently)
      const promptBase = promptOverride ?? trimmed;
      let promptWithFiles = promptBase;
      if (attachedFiles.length > 0) {
        const fileRefs = attachedFiles
          .map(
            (f) =>
              `${f.uri} (${MCPService.formatFileSize(f.size)}, ${f.mimetype})`
          )
          .join(", ");
        promptWithFiles = `[Attached files: ${fileRefs}]\n\n${promptBase}`;
      }

      // Get last session ID for conversation continuity. Error rows can carry a dead
      // router session id (the failed turn's placeholder kept it), so skip them.
      const lastSessionId =
        [...messagesBeforeOptimistic]
          .reverse()
          .find((m) => m.sessionId && m.status !== "error")?.sessionId ?? null;

      // Trigger async AI response - Lambda will self-invoke and create the AI message.
      // The response will arrive via GraphQL subscription. The system prompt is resolved
      // server-side (issue #694); persona and the onboarding flag are the client's only inputs.
      await MCPService.sendMessageAsync(
        // Base codename only — an admin's tier pin travels separately as modelOverride.
        model || modelCatalog?.default || FALLBACK_CLIENT_MODEL,
        promptWithFiles,
        conversationForSaving?.id ?? null,
        {
          sessionId: lastSessionId,
          persona,
          // Onboarding guidance is appended once per user — during the chat tour, on a deferred
          // first-ever message, or when a message reads new-user.
          onboarding: Boolean(onboarding),
          // Debug reasoning is an admin-only capability; only forward the flag when both
          // the toggle is on and the sender is an admin.
          debug: Boolean(debug && isAdmin),
          // Same for the model pin — only an admin's picker selection is worth sending.
          modelOverride: isAdmin ? modelOverride : undefined,
        }
      );

      // Return immediately - AI response will arrive via subscription
      // Keep isSending=true (set by pending reducer) until subscription receives the message
      return {
        userMessage: {
          id: `${Date.now()}`,
          text: trimmed,
          isUser: true,
          attachments:
            attachedFiles.length > 0 ? [...attachedFiles] : undefined,
          createdAt: new Date().toISOString(),
        } as ChatMessage,
        conversation: conversationForSaving,
        isNewConversation: isFirstMessage && !currentConversation,
        // Track which conversation this send was for to guard against navigation during async
        startingConversationId,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to send message",
      } as SendMessageError);
    }
  }
);

// Delete conversation
export const deleteConversation = createAsyncThunk(
  "aiChat/deleteConversation",
  async (conversationId: string, thunkAPI) => {
    try {
      await ConversationService.deleteConversation(conversationId);
      return conversationId;
    } catch (error) {
      return thunkAPI.rejectWithValue("Failed to delete conversation");
    }
  }
);

// Mark conversation as read — optimistic; rejected handler rolls back the prior count.
// previousUnreadCount is passed in by the caller since it must be captured before
// the .pending reducer applies the optimistic update (thunkAPI.getState() inside the
// payload creator would see the already-mutated value).
export const markConversationAsRead = createAsyncThunk(
  "aiChat/markConversationAsRead",
  async ({ conversationId, previousUnreadCount }: { conversationId: string; previousUnreadCount: number }, thunkAPI) => {
    try {
      await ConversationService.markConversationAsRead(conversationId);
      return conversationId;
    } catch (error) {
      return thunkAPI.rejectWithValue({ conversationId, previousUnreadCount });
    }
  }
);

// Mark conversation as unread — optimistic; rejected handler rolls back the prior count.
// previousUnreadCount is passed in by the caller (see markConversationAsRead comment above).
export const markConversationAsUnread = createAsyncThunk(
  "aiChat/markConversationAsUnread",
  async ({ conversationId, previousUnreadCount }: { conversationId: string; previousUnreadCount: number }, thunkAPI) => {
    try {
      await ConversationService.markConversationAsUnread(conversationId);
      return conversationId;
    } catch (error) {
      return thunkAPI.rejectWithValue({ conversationId, previousUnreadCount });
    }
  }
);

// Rename conversation — optimistic; rejected handler restores the prior title.
// previousTitle is passed in by the caller (see markConversationAsRead comment above).
export const renameConversation = createAsyncThunk(
  "aiChat/renameConversation",
  async ({ conversationId, title, previousTitle }: { conversationId: string; title: string; previousTitle: string }, thunkAPI) => {
    try {
      await ConversationService.renameConversation(conversationId, title);
      return { conversationId, title };
    } catch (error) {
      return thunkAPI.rejectWithValue({ conversationId, previousTitle });
    }
  }
);

// Upload file
export const uploadFile = createAsyncThunk(
  "aiChat/uploadFile",
  async ({ file, conversationId }: { file: File; conversationId: string }, thunkAPI) => {
    try {
      const result = await MCPService.uploadFile(file, conversationId);
      return { attachedFile: result as AttachedFile, conversationId };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error instanceof Error ? error.message : "Failed to upload file"
      );
    }
  }
);

// Slice
const aiChatSlice = createSlice({
  name: "aiChat",
  initialState,
  reducers: {
    // Input management
    setInputValue: (state, action: PayloadAction<string>) => {
      state.inputValue = action.payload;
    },
    setModel: (state, action: PayloadAction<ChatModelId>) => {
      state.model = action.payload;
    },
    setModelOverride: (state, action: PayloadAction<ChatModelId>) => {
      state.modelOverride = action.payload;
    },
    setDebug: (state, action: PayloadAction<boolean>) => {
      state.debug = action.payload;
    },

    // File management
    removeAttachedFile: (state, action: PayloadAction<string>) => {
      state.attachedFiles = state.attachedFiles.filter(
        (f) => f.uri !== action.payload
      );
    },
    clearAttachedFiles: (state) => {
      state.attachedFiles = [];
    },

    // Conversation management
    startNewConversation: (state) => {
      // Save the outgoing draft, then clear — a new chat must start with an empty composer
      if (state.currentConversation?.id) {
        state.drafts[state.currentConversation.id] = state.inputValue;
      }
      state.inputValue = "";
      state.messages = [];
      state.currentConversation = null;
      state.activeConversationTokens = 0;
      state.sendError = null;
      state.attachedFiles = [];
      state.uploadingCount = 0;
      // Reset sending state to prevent stuck "Thinking..." indicator
      state.isSending = false;
      state.pendingSendConversationId = null;
      // Generate a fresh UUID for the new chat session
      state.pendingConversationId = crypto.randomUUID();
    },

    setProposalStatus: (state, action: PayloadAction<{ conversationId: string; messageId: string; status: 'pending' | 'approved' | 'declined' }>) => {
      const { conversationId, messageId, status } = action.payload;
      if (!state.proposalStatuses[conversationId]) state.proposalStatuses[conversationId] = {};
      state.proposalStatuses[conversationId][messageId] = status;
      try { localStorage.setItem('proposalStatuses', JSON.stringify(state.proposalStatuses)); } catch { /* storage unavailable */ }
    },
    setProposalSelectedGroup: (state, action: PayloadAction<{ conversationId: string; messageId: string; groupId: string }>) => {
      const { conversationId, messageId, groupId } = action.payload;
      if (!state.proposalSelectedGroups[conversationId]) state.proposalSelectedGroups[conversationId] = {};
      state.proposalSelectedGroups[conversationId][messageId] = groupId;
      try { localStorage.setItem('proposalSelectedGroups', JSON.stringify(state.proposalSelectedGroups)); } catch { /* storage unavailable */ }
    },
    setProposalAssetImages: (state, action: PayloadAction<{ conversationId: string; messageId: string; images: (string | null)[] }>) => {
      const { conversationId, messageId, images } = action.payload;
      if (!state.proposalAssetImages[conversationId]) state.proposalAssetImages[conversationId] = {};
      state.proposalAssetImages[conversationId][messageId] = images;
      try { localStorage.setItem('proposalAssetImages', JSON.stringify(state.proposalAssetImages)); } catch { /* storage unavailable */ }
    },
    setProposalGroupImage: (state, action: PayloadAction<{ conversationId: string; messageId: string; imageUrl: string | null }>) => {
      const { conversationId, messageId, imageUrl } = action.payload;
      if (!state.proposalGroupImages[conversationId]) state.proposalGroupImages[conversationId] = {};
      state.proposalGroupImages[conversationId][messageId] = imageUrl;
      try { localStorage.setItem('proposalGroupImages', JSON.stringify(state.proposalGroupImages)); } catch { /* storage unavailable */ }
    },
    setProposalAssetEdits: (state, action: PayloadAction<{ conversationId: string; messageId: string; edits: Array<{ name: string; description: string }> }>) => {
      const { conversationId, messageId, edits } = action.payload;
      if (!state.proposalAssetEdits[conversationId]) state.proposalAssetEdits[conversationId] = {};
      state.proposalAssetEdits[conversationId][messageId] = edits;
      try { localStorage.setItem('proposalAssetEdits', JSON.stringify(state.proposalAssetEdits)); } catch { /* storage unavailable */ }
    },
    setProposalGroupEditAction: (state, action: PayloadAction<{ conversationId: string; messageId: string; edit: { name: string; description: string } }>) => {
      const { conversationId, messageId, edit } = action.payload;
      if (!state.proposalGroupEdit[conversationId]) state.proposalGroupEdit[conversationId] = {};
      state.proposalGroupEdit[conversationId][messageId] = edit;
      try { localStorage.setItem('proposalGroupEdit', JSON.stringify(state.proposalGroupEdit)); } catch { /* storage unavailable */ }
    },
    setProposalApprovedGroupId: (state, action: PayloadAction<{ conversationId: string; messageId: string; groupId: string }>) => {
      const { conversationId, messageId, groupId } = action.payload;
      if (!state.proposalApprovedGroupIds[conversationId]) state.proposalApprovedGroupIds[conversationId] = {};
      state.proposalApprovedGroupIds[conversationId][messageId] = groupId;
      try { localStorage.setItem('proposalApprovedGroupIds', JSON.stringify(state.proposalApprovedGroupIds)); } catch { /* storage unavailable */ }
    },
    setProposalDeploySchemaId: (state, action: PayloadAction<{ conversationId: string; messageId: string; schemaId: string }>) => {
      const { conversationId, messageId, schemaId } = action.payload;
      if (!state.proposalDeploySchemaIds[conversationId]) state.proposalDeploySchemaIds[conversationId] = {};
      state.proposalDeploySchemaIds[conversationId][messageId] = schemaId;
      try { localStorage.setItem('proposalDeploySchemaIds', JSON.stringify(state.proposalDeploySchemaIds)); } catch { /* storage unavailable */ }
    },

    // Clear error
    clearSendError: (state) => {
      state.sendError = null;
    },

    // Clear pending send state (used when message arrives or timeout occurs)
    clearPendingSend: (state) => {
      state.isSending = false;
      state.pendingSendConversationId = null;
      state.pendingSendTimestamp = null;
    },

    // Handle message timeout - show error and reset state
    handleMessageTimeout: (state) => {
      state.isSending = false;
      state.sendError = "Response timed out. Please try again.";
      state.pendingSendConversationId = null;
      state.pendingSendTimestamp = null;
    },

    // Add AI message from subscription (async flow)
    addAiMessageFromSubscription: (
      state,
      action: PayloadAction<{
        conversationId: string;
        message: ChatMessage;
      }>
    ) => {
      const { conversationId, message } = action.payload;
      const isViewingThisConversation = state.currentConversation?.id === conversationId;

      // Fall back to user-facing copy when the AI returns an empty response,
      // otherwise the bubble renders with no body (just the ERODE header).
      // A "pending" placeholder is intentionally empty (renders as "Thinking..."),
      // so don't normalize it into the empty-response fallback.
      const normalizedMessage: ChatMessage = {
        ...message,
        text:
          message.status === "pending"
            ? message.text
            : normalizeAiMessageText(message.text, message.isUser),
      };

      // Only add message to UI if we're viewing this conversation
      if (isViewingThisConversation) {
        // Check if message already exists (prevent duplicates)
        const exists = state.messages.some((m) => m.id === normalizedMessage.id);
        if (!exists) {
          state.messages.push(normalizedMessage);

          // Update token count
          state.activeConversationTokens +=
            (message.inputTokens ?? 0) + (message.outputTokens ?? 0);
        }

        // Stop showing "Thinking..." indicator and clear timeout tracking
        state.isSending = false;
        state.pendingSendTimestamp = null;
      }

      // Update conversation in sidebar
      const conv = state.conversations.find((c) => c.id === conversationId);
      if (conv) {
        conv.messageCount += 1;
        conv.lastMessageAt = new Date().toISOString();

        // Increment unread count only if NOT viewing this conversation
        if (!isViewingThisConversation) {
          conv.unreadCount += 1;
        }
      }
    },

    // Update an existing AI message in place (async flow): a "pending" placeholder
    // is resolved to "complete" (the answer) or "error" via onUpdateMCPMessageAll.
    updateAiMessageFromSubscription: (
      state,
      action: PayloadAction<{
        conversationId: string;
        messageId: string;
        updates: Partial<ChatMessage>;
      }>
    ) => {
      const { conversationId, messageId, updates } = action.payload;
      const isViewingThisConversation =
        state.currentConversation?.id === conversationId;
      if (!isViewingThisConversation) {
        return;
      }

      const idx = state.messages.findIndex((m) => m.id === messageId);
      if (idx === -1) {
        return;
      }

      const existing = state.messages[idx];
      // Normalize incoming text the same way as new messages, but leave a
      // still-pending update untouched (it has no body yet).
      const nextText =
        updates.text !== undefined && updates.status !== "pending"
          ? normalizeAiMessageText(updates.text, existing.isUser)
          : updates.text ?? existing.text;

      state.messages[idx] = { ...existing, ...updates, text: nextText };

      // Everything below is the placeholder → answer transition. Later updates to the same
      // row (e.g. the owner rating it) re-fire this subscription with the same tokens/status
      // and must not double-count tokens or clear an unrelated in-flight send.
      const wasPending = existing.status === "pending";

      // Account for tokens now that the final message has arrived (the placeholder
      // carried none, so there's no double-counting).
      if (wasPending && (updates.inputTokens !== undefined || updates.outputTokens !== undefined)) {
        state.activeConversationTokens +=
          (updates.inputTokens ?? 0) + (updates.outputTokens ?? 0);
      }

      // A terminal status means the reply (or error) arrived — stop "Thinking...".
      if (wasPending && (updates.status === "complete" || updates.status === "error")) {
        state.isSending = false;
        state.pendingSendTimestamp = null;
        state.pendingSendConversationId = null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Model catalog — seed the picker with the server's default the first time it lands.
      .addCase(fetchModelCatalog.fulfilled, (state, action) => {
        state.modelCatalog = action.payload;
        if (!state.model || !action.payload.models.some((m) => m.id === state.model)) {
          state.model = action.payload.default;
        }
      })
      // Leave `model` on its seeded fallback so a catalog outage degrades the picker, not sending.
      .addCase(fetchModelCatalog.rejected, (state) => {
        state.modelCatalog = null;
      })
      // fetchConversations
      .addCase(fetchConversations.pending, (state) => {
        state.conversationsLoading = "pending";
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.conversationsLoading = "success";
        state.conversations = action.payload;
      })
      .addCase(fetchConversations.rejected, (state) => {
        state.conversationsLoading = "failed";
      })

      // selectConversation
      .addCase(selectConversation.pending, (state) => {
        state.messagesLoading = "pending";
      })
      .addCase(selectConversation.fulfilled, (state, action) => {
        state.messagesLoading = "success";
        // Save draft for the conversation we're leaving
        if (state.currentConversation?.id) {
          state.drafts[state.currentConversation.id] = state.inputValue;
        }
        state.messages = action.payload.messages;
        state.currentConversation = action.payload.conversation;
        state.activeConversationTokens = action.payload.totalTokens;
        // Restore draft for the conversation we're entering
        state.inputValue = state.drafts[action.payload.conversation.id] ?? "";
        state.attachedFiles = [];
        state.uploadingCount = 0;
        try { state.proposalStatuses = JSON.parse(localStorage.getItem('proposalStatuses') || '{}'); } catch { state.proposalStatuses = {}; }
        try { state.proposalSelectedGroups = JSON.parse(localStorage.getItem('proposalSelectedGroups') || '{}'); } catch { state.proposalSelectedGroups = {}; }
        try { state.proposalAssetImages = JSON.parse(localStorage.getItem('proposalAssetImages') || '{}'); } catch { state.proposalAssetImages = {}; }
        try { state.proposalGroupImages = JSON.parse(localStorage.getItem('proposalGroupImages') || '{}'); } catch { state.proposalGroupImages = {}; }
        try { state.proposalAssetEdits = JSON.parse(localStorage.getItem('proposalAssetEdits') || '{}'); } catch { state.proposalAssetEdits = {}; }
        try { state.proposalGroupEdit = JSON.parse(localStorage.getItem('proposalGroupEdit') || '{}'); } catch { state.proposalGroupEdit = {}; }
        try { state.proposalApprovedGroupIds = JSON.parse(localStorage.getItem('proposalApprovedGroupIds') || '{}'); } catch { state.proposalApprovedGroupIds = {}; }
        try { state.proposalDeploySchemaIds = JSON.parse(localStorage.getItem('proposalDeploySchemaIds') || '{}'); } catch { state.proposalDeploySchemaIds = {}; }
        // The "Thinking..." state now lives on the message row itself (a "pending"
        // status placeholder), so the transient isSending flag is redundant once we
        // hold the authoritative message list. Always clear it on a successful fetch
        // — this makes returning to a conversation self-healing and prevents a stuck
        // indicator. A genuinely outstanding reply still renders from its persisted
        // "pending" row.
        state.isSending = false;
        state.pendingSendTimestamp = null;
        state.pendingSendConversationId = null;
        // Reset unread count when viewing conversation (optimistic update)
        const conv = state.conversations.find((c) => c.id === action.payload.conversation.id);
        if (conv && conv.unreadCount > 0) {
          conv.unreadCount = 0;
        }
      })
      .addCase(selectConversation.rejected, (state) => {
        state.messagesLoading = "failed";
      })

      // sendMessage
      .addCase(sendMessage.pending, (state, action) => {
        state.isSending = true;
        state.sendError = null;
        // Track which conversation this send belongs to (for guarding rejected case)
        state.pendingSendConversationId = state.currentConversation?.id ?? null;
        // Track when message was sent for timeout detection
        state.pendingSendTimestamp = new Date().toISOString();
        // Optimistically add user message using the input passed to the thunk
        const trimmed = action.meta.arg.inputValue.trim();
        if (trimmed) {
          state.messages.push({
            id: `${Date.now()}-pending`,
            text: trimmed,
            isUser: true,
            attachments:
              action.meta.arg.attachedFilesInput.length > 0
                ? [...action.meta.arg.attachedFilesInput]
                : undefined,
            createdAt: new Date().toISOString(),
          });
          state.inputValue = "";
          if (state.currentConversation?.id) {
            delete state.drafts[state.currentConversation.id];
          }
          state.attachedFiles = [];
        }
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        // Keep isSending=true - it will be set to false when subscription receives AI message
        // This shows "Thinking..." indicator while waiting for async response
        state.pendingSendConversationId = null;

        // Guard against navigation during async: only update messages if still viewing the same conversation
        const currentId = state.currentConversation?.id ?? null;
        const startingId = action.payload.startingConversationId;
        const isStillSameConversation = currentId === startingId;

        if (isStillSameConversation) {
          // Remove pending user message and add final user message
          state.messages = state.messages.filter(
            (m) => !m.id.endsWith("-pending")
          );
          state.messages.push(action.payload.userMessage);

          // Update conversation metadata
          if (action.payload.conversation) {
            state.currentConversation = action.payload.conversation;
          }
        }

        // Always update the conversation list (sidebar) regardless of current view
        if (action.payload.isNewConversation && action.payload.conversation) {
          state.conversations.unshift({
            id: action.payload.conversation.id,
            title: action.payload.conversation.title,
            model: action.payload.conversation.model,
            modelCodename: action.payload.conversation.modelCodename,
            messageCount: 1, // Just user message for now, AI will be added via subscription
            unreadCount: 0, // New conversation starts with no unread
            lastMessageAt: new Date().toISOString(),
          });
        } else if (action.payload.conversation) {
          // Update existing conversation in list (user message only)
          const conv = state.conversations.find(
            (c) => c.id === action.payload.conversation!.id
          );
          if (conv) {
            conv.messageCount += 1;
            conv.lastMessageAt = new Date().toISOString();
          }
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.isSending = false;
        // Extract error message from structured payload
        const payload = action.payload as SendMessageRejection | undefined;
        if (payload?.type === "no_subscription") {
          state.sendError = "No subscription";
        } else if (payload?.type === "limit_exceeded") {
          state.sendError = "Token limit exceeded";
        } else if (payload?.type === "error") {
          state.sendError = payload.message;
        } else {
          state.sendError = action.error?.message ?? "Failed to send message";
        }

        // Guard against navigation during async: only remove pending message if still viewing the same conversation
        // For no_subscription, keep the message visible so user sees what they tried to send
        const currentId = state.currentConversation?.id ?? null;
        const pendingId = state.pendingSendConversationId;
        const keepMessage = payload?.type === "no_subscription";
        if (currentId === pendingId && !keepMessage) {
          state.messages = state.messages.filter(
            (m) => !m.id.endsWith("-pending")
          );
        }
        state.pendingSendConversationId = null;
      })

      // deleteConversation
      .addCase(deleteConversation.fulfilled, (state, action) => {
        const deletedId = action.payload;
        state.conversations = state.conversations.filter((c) => c.id !== deletedId);
        if (state.currentConversation?.id === deletedId) {
          state.currentConversation = null;
          state.messages = [];
          state.activeConversationTokens = 0;
        }
        delete state.proposalStatuses[deletedId];
        delete state.proposalSelectedGroups[deletedId];
        delete state.proposalAssetImages[deletedId];
        delete state.proposalGroupImages[deletedId];
        delete state.proposalAssetEdits[deletedId];
        delete state.proposalGroupEdit[deletedId];
        delete state.proposalApprovedGroupIds[deletedId];
        delete state.proposalDeploySchemaIds[deletedId];
        try { localStorage.setItem('proposalStatuses', JSON.stringify(state.proposalStatuses)); } catch { /* storage unavailable */ }
        try { localStorage.setItem('proposalSelectedGroups', JSON.stringify(state.proposalSelectedGroups)); } catch { /* storage unavailable */ }
        try { localStorage.setItem('proposalAssetImages', JSON.stringify(state.proposalAssetImages)); } catch { /* storage unavailable */ }
        try { localStorage.setItem('proposalGroupImages', JSON.stringify(state.proposalGroupImages)); } catch { /* storage unavailable */ }
        try { localStorage.setItem('proposalAssetEdits', JSON.stringify(state.proposalAssetEdits)); } catch { /* storage unavailable */ }
        try { localStorage.setItem('proposalGroupEdit', JSON.stringify(state.proposalGroupEdit)); } catch { /* storage unavailable */ }
        try { localStorage.setItem('proposalApprovedGroupIds', JSON.stringify(state.proposalApprovedGroupIds)); } catch { /* storage unavailable */ }
        try { localStorage.setItem('proposalDeploySchemaIds', JSON.stringify(state.proposalDeploySchemaIds)); } catch { /* storage unavailable */ }
      })

      // markConversationAsRead — optimistic on pending, roll back the prior count on failure
      .addCase(markConversationAsRead.pending, (state, action) => {
        const conv = state.conversations.find((c) => c.id === action.meta.arg.conversationId);
        if (conv) {
          conv.unreadCount = 0;
        }
      })
      .addCase(markConversationAsRead.fulfilled, (state, action) => {
        const conv = state.conversations.find((c) => c.id === action.payload);
        if (conv) {
          conv.unreadCount = 0;
        }
      })
      .addCase(markConversationAsRead.rejected, (state, action) => {
        const payload = action.payload as { conversationId: string; previousUnreadCount: number } | undefined;
        if (!payload) return;
        const conv = state.conversations.find((c) => c.id === payload.conversationId);
        if (conv) {
          conv.unreadCount = payload.previousUnreadCount;
        }
      })

      // markConversationAsUnread — optimistic on pending, roll back the prior count on failure
      .addCase(markConversationAsUnread.pending, (state, action) => {
        const conv = state.conversations.find((c) => c.id === action.meta.arg.conversationId);
        if (conv) {
          conv.unreadCount = Math.max(conv.unreadCount, 1);
        }
      })
      .addCase(markConversationAsUnread.rejected, (state, action) => {
        const payload = action.payload as { conversationId: string; previousUnreadCount: number } | undefined;
        if (!payload) return;
        const conv = state.conversations.find((c) => c.id === payload.conversationId);
        if (conv) {
          conv.unreadCount = payload.previousUnreadCount;
        }
      })

      // renameConversation — optimistic on pending, restore the prior title on failure
      .addCase(renameConversation.pending, (state, action) => {
        const { conversationId, title } = action.meta.arg;
        const conv = state.conversations.find((c) => c.id === conversationId);
        if (conv) {
          conv.title = title;
        }
        if (state.currentConversation?.id === conversationId) {
          state.currentConversation.title = title;
        }
      })
      .addCase(renameConversation.rejected, (state, action) => {
        const payload = action.payload as { conversationId: string; previousTitle: string } | undefined;
        if (!payload) return;
        const conv = state.conversations.find((c) => c.id === payload.conversationId);
        if (conv) {
          conv.title = payload.previousTitle;
        }
        if (state.currentConversation?.id === payload.conversationId) {
          state.currentConversation.title = payload.previousTitle;
        }
      })

      // uploadFile (supports parallel uploads via counter)
      .addCase(uploadFile.pending, (state) => {
        state.uploadingCount += 1;
      })
      .addCase(uploadFile.fulfilled, (state, action) => {
        state.uploadingCount -= 1;
        // Only add the file if we're still on the same conversation it was uploaded for
        const activeConversationId = state.currentConversation?.id ?? state.pendingConversationId;
        if (action.payload.conversationId === activeConversationId) {
          state.attachedFiles.push(action.payload.attachedFile);
        }
      })
      .addCase(uploadFile.rejected, (state, action) => {
        state.uploadingCount -= 1;
        state.sendError = action.payload as string;
      });
  },
});

export const {
  setInputValue,
  setModel,
  setModelOverride,
  setDebug,
  removeAttachedFile,
  clearAttachedFiles,
  startNewConversation,
  clearSendError,
  clearPendingSend,
  handleMessageTimeout,
  addAiMessageFromSubscription,
  updateAiMessageFromSubscription,
  setProposalStatus,
  setProposalSelectedGroup,
  setProposalAssetImages,
  setProposalGroupImage,
  setProposalAssetEdits,
  setProposalGroupEditAction,
  setProposalApprovedGroupId,
  setProposalDeploySchemaId,
} = aiChatSlice.actions;

export default aiChatSlice.reducer;
